import React, { useState, useEffect, useRef } from "react";
import config from "../config";
import axios from "axios";
import jwt_decode from "jwt-decode";
import { notification, Modal } from "antd";
import { useTimer } from "react-timer-hook";
import ReactDOM from "react-dom/client";
import { CloseOutlined, ExclamationCircleFilled } from "@ant-design/icons";
import "./style.css";

const AuthContext = React.createContext({
  token: "",
  refreshToken: "",
  isLoggedIn: false,
  login: (token, refreshToken) => { },
});

export const AuthContextProvider = (props) => {
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem("refreshToken"));
  const tokenRef = useRef(token);

  const modalVisibleRef = useRef(false);
  const modalInstanceRef = useRef(null);
  const broadcastChannelRef = useRef(null);

  const userIsLoggedIn = !!token;

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const logoutUser = () => {
    setToken("");
    setRefreshToken("");
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("accessExp");
    localStorage.removeItem("refreshExp");
    sessionStorage.removeItem("refreshModalShown");
    localStorage.removeItem("refreshModalShown");

    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage("modal-close");
    }
  };

  const loginHandler = (token, refreshToken) => {
    const decodedAccess = jwt_decode(token);
    const decodedRefresh = jwt_decode(refreshToken);

    setToken(token);
    setRefreshToken(refreshToken);

    localStorage.setItem("token", token);
    localStorage.setItem("refreshToken", refreshToken);
    localStorage.setItem("accessExp", decodedAccess.exp);
    localStorage.setItem("refreshExp", decodedRefresh.exp);
    localStorage.setItem("refreshModalShown", "0");
  };

  const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    const response = await axios.post(config.backendHost + "auth/refresh_token", {
      refresh_token: refreshToken,
    });

    const newToken = response.data.jwt;
    const newRefreshToken = response.data.refresh_jwt;

    const decodedAccess = jwt_decode(newToken);
    const decodedRefresh = jwt_decode(newRefreshToken);

    tokenRef.current = newToken;

    localStorage.setItem("token", newToken);
    localStorage.setItem("refreshToken", newRefreshToken);
    localStorage.setItem("accessExp", decodedAccess.exp);
    localStorage.setItem("refreshExp", decodedRefresh.exp);
    sessionStorage.removeItem("refreshModalShown");

    return newToken;
  };

  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const currentToken = tokenRef.current;
        if (currentToken) {
          config.headers.Authorization = `Bearer ${currentToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401 && !error.config._retry) {
          error.config._retry = true;
          try {
            const newToken = await refreshAccessToken();
            error.config.headers.Authorization = `Bearer ${newToken}`;
            return axios.request(error.config);
          } catch (refreshError) {
            logoutUser();
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);


  useEffect(() => {
    let intervalId = null;
    const refreshExpStr = localStorage.getItem("refreshExp");
    if (!token || !refreshExpStr) return;

    const refreshExp = parseInt(refreshExpStr) * 1000;
    const warningThreshold = 5 * 60 * 1000;
    const idleThreshold = 55*60*1000;

    localStorage.setItem("lastActivityTime", Date.now().toString());

    const updateLastActivity = () => {
      const now = Date.now();
      localStorage.setItem("lastActivityTime", now.toString());
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage("activity");
      }
    };

    const handleBroadcastMessage = (event) => {
      const data = event.data;
      if (data === "activity") {
        localStorage.setItem("lastActivityTime", Date.now().toString());
      }

      if (data === "modal-close") {
        if (modalInstanceRef.current) {
          modalInstanceRef.current.destroy();
          modalInstanceRef.current = null;
        }
        modalVisibleRef.current = false;
        sessionStorage.removeItem("refreshModalShown");
      }
    };

    const checkSession = async () => {

      const now = Date.now();
      const lastActivity = parseInt(localStorage.getItem("lastActivityTime") || "0");
      const refreshExpnew = parseInt(localStorage.getItem("refreshExp") || "0") * 1000;
      const timeToExpire = refreshExpnew - now;
      const idleTime = now - lastActivity;
      if (
        timeToExpire <= warningThreshold &&
        idleTime >= idleThreshold &&
        !modalVisibleRef.current
      ) {
        modalVisibleRef.current = true;
        if (localStorage.getItem("refreshModalShown") === "0") {
          showSessionExtendModal().finally(() => {
            modalVisibleRef.current = false;
          });
        }
      }
      if (
        timeToExpire <= warningThreshold &&
        idleTime <= idleThreshold &&
        !modalVisibleRef.current
      ) {
        try {
          if (localStorage.getItem("refreshModalShown") === "0") {
          await refreshAccessToken();
          }
        } catch (err) {
          console.error("Failed to extend session:", err);
        }
      }
    };

    window.addEventListener("mousemove", updateLastActivity);
    window.addEventListener("keydown", updateLastActivity);

    if (window.BroadcastChannel) {
      broadcastChannelRef.current = new BroadcastChannel("user_activity");
      broadcastChannelRef.current.addEventListener("message", handleBroadcastMessage);
    }

    intervalId = setInterval(checkSession, 1000); // check every 15s

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("mousemove", updateLastActivity);
      window.removeEventListener("keydown", updateLastActivity);
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.close();
      }
    };
  }, [token]);

  const showSessionExtendModal = () => {
    const refreshExpStr = localStorage.getItem("refreshExp");
    if (!refreshExpStr) return;

    const expiryTimestamp = new Date(parseInt(refreshExpStr) * 1000);
    const TimerModalContent = () => {
      const { seconds, minutes } = useTimer({
        expiryTimestamp,
        onExpire: () => {
          if (modalInstanceRef.current) {
            modalInstanceRef.current.destroy();
            modalInstanceRef.current = null;
          }
          modalVisibleRef.current = false;
          logoutUser(); // auto logout
        },
      });
      return (
        <div style={{ textAlign: "center", paddingTop: 7, paddingBottom: 7, borderBottom: "1px solid #f0f0f0" }}>
          <ExclamationCircleFilled style={{ fontSize: 30, color: "#faad14" }} />
          <h2 style={{ marginTop: 16, fontWeight: 500 }}>
            We’ll sign you out in{" "}
            <span style={{ fontWeight: "bold" }}>
              {String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")}
            </span>
          </h2>
          <p style={{ marginTop: 10, fontSize: 14, color: "#555", maxWidth: 400, margin: "10px auto", }}>
            We sign you out of your account when you're inactive for 1 hour. Do you want to extend it?
          </p>
        </div>
      );
    };
    modalInstanceRef.current = Modal.confirm({
      icon: null,
      title: (
        <div style={{ paddingLeft: "10px", display: 'flex', justifyContent:"space-between",  fontSize: 18, borderBottom: "1px solid #f0f0f0", paddingBottom: 8 }}>
          <span>Session Timeout</span>
          <CloseOutlined
            onClick={() => {
              if (modalInstanceRef.current) {
                modalInstanceRef.current.destroy(); // 🔥 THIS IS MISSING!
                modalInstanceRef.current = null;
              }
              if (broadcastChannelRef.current) {
                broadcastChannelRef.current.postMessage("modal-close");
              }
              localStorage.setItem("refreshModalShown", "1");
              modalVisibleRef.current = false;
            }}
            style={{
              fontSize: 18,
              color: "rgb(183 183 183)",
              fontWeight: 200, // thin
              cursor: "pointer",
              marginRight: "3%"
            }}
          />
        </div>
      ),
      content: <TimerModalContent />,
      okText: "Extend",
      cancelText: "Ignore",
      className: "modal-css",
      okButtonProps: {
        type: "primary",
        style: { minWidth: 80 }
      },
      cancelButtonProps: {
        style: { minWidth: 80 }
      },
      onOk: async () => {
        try {
          await refreshAccessToken();
          notification.success({ message: "Session extended." });
          if (broadcastChannelRef.current) {
            broadcastChannelRef.current.postMessage("modal-close");
          }
        } catch (err) {
          console.error("Failed to extend session:", err);
        }
        modalVisibleRef.current = false;
      },
      onCancel: () => {
        if (broadcastChannelRef.current) {
          broadcastChannelRef.current.postMessage("modal-close");
        }
        localStorage.setItem("refreshModalShown", "1");
        modalVisibleRef.current = false;
      },
    });
  };

  return (
    <AuthContext.Provider
      value={{ token, refreshToken, isLoggedIn: userIsLoggedIn, login: loginHandler }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};

export default AuthContext;